import React from 'react'
import { PeekSpaceResponse } from '@harmony/live-types'
import { Icon, Stack, Text } from '@ui'
import { useLiveEnabled, useLivePeekSpace } from 'hooks/useLivePeek'

import * as styles from './LiveIndicatorPill.css'

/** Format count to max 4 characters (e.g., 999, 1.2k, 10k, 999k) */
function formatCount(count: number): string {
    if (count < 1000) {
        return String(count)
    }
    if (count < 10000) {
        return `${(count / 1000).toFixed(1)}k`
    }
    if (count < 1000000) {
        return `${Math.floor(count / 1000)}k`
    }
    return `${(count / 1000000).toFixed(1)}m`
}

interface LiveIndicatorPillProps {
    spaceId: string
    /** Show subscriber count (online users) */
    showSubscribers?: boolean
    /** Compact mode - just show icon and total count */
    compact?: boolean
}

/**
 * Live indicator pill that shows stage/listener counts for a space
 * Only renders if live is enabled and there's activity
 */
export const LiveIndicatorPill = React.memo(function LiveIndicatorPill(
    props: LiveIndicatorPillProps,
) {
    const { spaceId, showSubscribers = false, compact = false } = props
    const enabled = useLiveEnabled()
    const { data: peekData, isLoading } = useLivePeekSpace(enabled ? spaceId : undefined)

    // Don't render if live is not enabled or still loading
    if (!enabled || isLoading || !peekData) {
        return null
    }

    const { stageCount, listenerCount, subscriberCount } = peekData

    // Don't render if no activity
    if (stageCount === 0 && listenerCount === 0) {
        return null
    }

    const hasStage = stageCount > 0

    if (compact) {
        const totalActive = showSubscribers ? subscriberCount : stageCount + listenerCount
        return (
            <Stack
                horizontal
                gap="xxs"
                alignItems="center"
                background={hasStage ? 'positive' : 'level3'}
                paddingX="sm"
                paddingY="xxs"
                rounded="full"
                className={hasStage ? styles.liveGlowClass : undefined}
            >
                <Icon
                    type="microphone"
                    size="square_xs"
                    color={hasStage ? 'inverted' : 'gray2'}
                    className={hasStage ? styles.pulsingIconClass : undefined}
                />
                <Text size="xs" fontWeight="medium" color={hasStage ? 'inverted' : 'gray2'}>
                    {formatCount(totalActive)}
                </Text>
            </Stack>
        )
    }

    return (
        <Stack
            horizontal
            gap="xs"
            alignItems="center"
            background={hasStage ? 'positive' : 'level3'}
            paddingX="sm"
            paddingY="xxs"
            rounded="full"
            className={hasStage ? styles.liveGlowClass : undefined}
        >
            {stageCount > 0 && (
                <Stack horizontal gap="xxs" alignItems="center">
                    <Icon
                        type="microphone"
                        size="square_xs"
                        color={hasStage ? 'inverted' : 'gray2'}
                        className={hasStage ? styles.pulsingIconClass : undefined}
                    />
                    <Text size="xs" fontWeight="medium" color={hasStage ? 'inverted' : 'gray2'}>
                        {formatCount(stageCount)}
                    </Text>
                </Stack>
            )}
            {listenerCount > 0 && (
                <Stack horizontal gap="xxs" alignItems="center">
                    <Icon type="eye" size="square_xs" color={hasStage ? 'inverted' : 'gray2'} />
                    <Text size="xs" fontWeight="medium" color={hasStage ? 'inverted' : 'gray2'}>
                        {formatCount(listenerCount)}
                    </Text>
                </Stack>
            )}
            {showSubscribers && subscriberCount > 0 && (
                <Stack horizontal gap="xxs" alignItems="center">
                    <Icon type="member" size="square_xs" color={hasStage ? 'inverted' : 'gray2'} />
                    <Text size="xs" fontWeight="medium" color={hasStage ? 'inverted' : 'gray2'}>
                        {formatCount(subscriberCount)}
                    </Text>
                </Stack>
            )}
        </Stack>
    )
})

/**
 * Hook to get live peek data for a space (for use in custom components)
 */
export function useLivePeekData(spaceId: string | undefined): {
    data: PeekSpaceResponse | undefined
    isLoading: boolean
    hasActivity: boolean
    enabled: boolean
} {
    const enabled = useLiveEnabled()
    const { data, isLoading } = useLivePeekSpace(enabled && spaceId ? spaceId : undefined)

    const hasActivity = !!data && (data.stageCount > 0 || data.listenerCount > 0)

    return {
        data,
        isLoading,
        hasActivity,
        enabled,
    }
}
